// ── User activity: the shapes and the arithmetic ────────────────────────────
//
// Presence is recorded by a heartbeat from the browser and read back through
// /api/activity, which is the only door into public.user_sessions - the table
// has no RLS policies, so nothing here can reach it with a user session.
//
// Everything in this file is pure apart from the hook at the bottom, so the
// rollup arithmetic is testable without a database or a signed-in user.

import { useEffect } from 'react';
import { apiPost } from '@/lib/apiFetch';

/** One person's activity over the requested window. Mirrors user_activity_summary. */
export interface UserActivityRow {
  technician_id: string;
  user_id: string;
  name: string | null;
  email: string | null;
  role: string | null;
  is_active: boolean;
  login_count: number;
  first_seen_at: string | null;
  /** Latest presence of all time, not only within the window. May predate it. */
  last_seen_at: string | null;
  active_seconds: number;
  action_count: number;
  last_action_at: string | null;
}

/** One day of the window. Mirrors user_activity_daily; quiet days are present as zeroes. */
export interface ActivityDay {
  day: string;
  login_count: number;
  active_users: number;
  action_count: number;
}

/** One sign-in. Mirrors a public.user_sessions row. */
export interface ActivitySession {
  id: string;
  user_id: string;
  technician_id: string | null;
  started_at: string;
  last_seen_at: string;
  active_seconds: number;
  user_agent: string | null;
  ip_address: string | null;
}

export interface ActivitySummary {
  days: number;
  onlineWindowSeconds: number;
  /** The server's clock. Every "ago" on the page is measured against this. */
  now: string;
  users: UserActivityRow[];
  daily: ActivityDay[];
  recentSessions: ActivitySession[];
}

/** Windows the API will compute. Anything else is a 400. */
export const ACTIVITY_WINDOWS = [7, 30, 90, 365] as const;
export type ActivityWindow = (typeof ACTIVITY_WINDOWS)[number];

/** How often the browser reports in. Must stay well under the API's 180s gap tolerance. */
const BEAT_INTERVAL_MS = 60_000;

// ── Arithmetic ──────────────────────────────────────────────────────────────

/** Milliseconds between two instants, or null if either is absent or unparseable. */
function msBetween(from: string | null | undefined, to: string): number | null {
  if (!from) return null;
  const then = new Date(from).getTime();
  const now = new Date(to).getTime();
  if (!Number.isFinite(then) || !Number.isFinite(now)) return null;
  return now - then;
}

/**
 * Is this person using the app right now?
 *
 * Measured against the server's clock, deliberately: the rows were written by
 * that clock, and a workstation a few minutes fast would otherwise show
 * everybody - including whoever is reading the page - as offline.
 */
export function isOnlineNow(
  lastSeenAt: string | null | undefined,
  now: string,
  windowSeconds: number,
): boolean {
  const ms = msBetween(lastSeenAt, now);
  if (ms === null) return false;
  return ms < windowSeconds * 1000;
}

/**
 * Time in the app, as hours and minutes.
 *
 * Minutes are kept at every scale. "61h" for a month of work hides the
 * difference between two people who differ by half a day.
 */
export function formatActiveTime(seconds: number | null | undefined): string {
  if (!seconds || seconds < 0) return '—';
  if (seconds < 60) return '<1m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (!hours) return `${minutes}m`;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

/**
 * How long ago, against the server's clock.
 *
 * Not lib/stationStatus's formatLastSeen, which reads Date.now() internally.
 * That is right for a station heartbeat arriving in real time and wrong here,
 * where the whole page is one snapshot taken at a known server instant.
 */
export function formatAgo(at: string | null | undefined, now: string): string {
  const ms = msBetween(at, now);
  if (ms === null) return 'Never';
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} hr ago`;
  const days = Math.floor(seconds / 86_400);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
}

/**
 * Days since this account was last seen, or null if it never has been.
 *
 * Floor, so "signed in six hours ago" is 0 days rather than rounding up into
 * looking like yesterday.
 */
export function daysSinceSeen(row: UserActivityRow, now: string): number | null {
  const ms = msBetween(row.last_seen_at, now);
  if (ms === null) return null;
  return Math.floor(ms / 86_400_000);
}

/**
 * An account nobody is using.
 *
 * Never seen counts, and is the more interesting half: it means the login was
 * created and never once used, which an "inactive for N days" test alone would
 * report as merely stale. Deactivated accounts are excluded - they are meant
 * to be idle, and listing them as dormant buries the live ones that are not.
 */
export function isDormant(row: UserActivityRow, now: string, thresholdDays = 30): boolean {
  if (!row.is_active) return false;
  const days = daysSinceSeen(row, now);
  return days === null || days >= thresholdDays;
}

export interface ActivityTotals {
  /** People with a session heard from inside the online window. */
  activeNow: number;
  /** People who signed in at some point today, on the server's date. */
  signedInToday: number;
  /** Active accounts not seen for the dormancy threshold, or never seen at all. */
  dormant: number;
  /** Active accounts that have never signed in once. */
  neverSignedIn: number;
  /** Sum of everybody's time in the app over the window. */
  totalActiveSeconds: number;
  /** Sum of everybody's audited actions over the window. */
  totalActions: number;
}

/**
 * The headline numbers.
 *
 * One pass over the rows rather than six filters, because every one of these
 * is answered from the same row and the page shows them together.
 */
export function activityTotals(summary: ActivitySummary, dormantAfterDays = 30): ActivityTotals {
  const today = summary.now.slice(0, 10);

  const totals = summary.users.reduce<ActivityTotals>((acc, row) => {
    if (isOnlineNow(row.last_seen_at, summary.now, summary.onlineWindowSeconds)) acc.activeNow++;
    if (isDormant(row, summary.now, dormantAfterDays)) acc.dormant++;
    if (row.is_active && !row.last_seen_at) acc.neverSignedIn++;
    acc.totalActiveSeconds += row.active_seconds || 0;
    acc.totalActions += row.action_count || 0;
    return acc;
  }, {
    activeNow: 0,
    signedInToday: 0,
    dormant: 0,
    neverSignedIn: 0,
    totalActiveSeconds: 0,
    totalActions: 0,
  });

  // Today's row of the daily rollup, not a date test on last_seen_at. The two
  // differ for a tab that has been open since yesterday: it is still beating,
  // so its last_seen_at is today, but nobody signed in. The rollup counts
  // sessions that *started* today, which is what the label claims.
  totals.signedInToday = summary.daily.find(d => d.day === today)?.active_users ?? 0;

  return totals;
}

/**
 * Browser and OS, from a user-agent string.
 *
 * A deliberately small guess: enough to tell a shop-floor workstation from
 * somebody's phone, which is the only question this column is asked. Order
 * matters - every Chromium browser claims to be Chrome, and Chrome claims to
 * be Safari, so the specific names have to be tested first.
 */
export function describeDevice(userAgent: string | null | undefined): string {
  if (!userAgent) return 'Unknown';

  const browser =
    /Edg\//.test(userAgent)     ? 'Edge' :
    /OPR\//.test(userAgent)     ? 'Opera' :
    /Firefox\//.test(userAgent) ? 'Firefox' :
    /Chrome\//.test(userAgent)  ? 'Chrome' :
    /Safari\//.test(userAgent)  ? 'Safari' :
    'Unknown';

  const os =
    /Windows/.test(userAgent)          ? 'Windows' :
    /Android/.test(userAgent)          ? 'Android' :
    /iPhone|iPad|iPod/.test(userAgent) ? 'iOS' :
    /Mac OS X/.test(userAgent)         ? 'macOS' :
    /Linux/.test(userAgent)            ? 'Linux' :
    '';

  if (browser === 'Unknown' && !os) return 'Unknown';
  return os ? `${browser} / ${os}` : browser;
}

/** Display name for a row, falling back to the email and then to the user id. */
export function displayName(row: UserActivityRow): string {
  return row.name?.trim() || row.email || row.user_id.slice(0, 8);
}

// ── Heartbeat ───────────────────────────────────────────────────────────────

/** Report once that this session is alive. Never throws - a missed beat is not an error. */
export async function sendBeat(): Promise<void> {
  try {
    await apiPost('/api/activity', { action: 'beat' });
  } catch {
    // Silent by design, like writeAuditLog. Presence is observability, and it
    // must never surface as a toast in front of somebody trying to work.
  }
}

/**
 * Report presence while this tab is in the foreground.
 *
 * Only while visible, which is what makes "time in app" mean anything. A tab
 * left open on a second monitor overnight stops beating, so the gap exceeds
 * the API's tolerance and the idle hours are not added to anybody's total.
 *
 * Beats immediately on becoming visible as well as on the interval, so the
 * sign-in row exists from the first moment rather than up to a minute later,
 * and so returning to the tab closes the gap before it is written off.
 */
export function useActivityHeartbeat(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setInterval> | undefined;

    const beatIfVisible = () => {
      if (document.visibilityState === 'visible') void sendBeat();
    };

    const start = () => {
      if (timer) return;
      beatIfVisible();
      timer = setInterval(beatIfVisible, BEAT_INTERVAL_MS);
    };

    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = undefined;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    onVisibilityChange();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stop();
    };
  }, [enabled]);
}
